import React, { useState } from 'react';
import {
  UserCheck,
  UserPlus,
  Phone,
  Mail,
  MessageSquare,
  Edit2,
  Trash2,
  X,
  BookOpen,
  Calendar,
  AlertTriangle,
} from 'lucide-react';
import { AdminTeacher, AdminRole, ScheduledClass } from '../types';
import { Badge, Button, Input, Tooltip } from '../../components/core';

interface TeachersManagementProps {
  teachers: AdminTeacher[];
  classes: ScheduledClass[];
  currentRole: AdminRole;
  onAddTeacher: (teacher: AdminTeacher) => void;
  onUpdateTeacher: (teacher: AdminTeacher) => void;
  onDeleteTeacher: (id: string) => void;
  addToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

export const TeachersManagement: React.FC<TeachersManagementProps> = ({
  teachers,
  classes,
  currentRole,
  onAddTeacher,
  onUpdateTeacher,
  onDeleteTeacher,
  addToast,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState<AdminTeacher | null>(null);

  const isAuditor = currentRole === 'Auditor';

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    title: 'Guruji',
    mobile: '',
    email: '',
    assignedLevels: [] as string[],
    assignedBatches: [] as string[],
    status: 'Active' as 'Active' | 'Inactive',
  });

  const availableLevels = ['Level 1 - Prarambhik', 'Level 2 - Madhyamik', 'Level 3 - Shravak Junior'];
  const availableBatches = ['Batch A - Weekend Morning', 'Batch B - Weekend Evening'];

  const openAddModal = () => {
    setEditingTeacher(null);
    setFormData({
      name: '',
      title: 'Guruji',
      mobile: '',
      email: '',
      assignedLevels: ['Level 1 - Prarambhik'],
      assignedBatches: ['Batch A - Weekend Morning'],
      status: 'Active',
    });
    setIsModalOpen(true);
  };

  const openEditModal = (t: AdminTeacher) => {
    if (isAuditor) {
      addToast('warning', 'Access Restricted', 'View-Only Auditors cannot edit teacher profiles.');
      return;
    }
    setEditingTeacher(t);
    setFormData({
      name: t.name,
      title: t.title,
      mobile: t.mobile,
      email: t.email,
      assignedLevels: [...t.assignedLevels],
      assignedBatches: [...t.assignedBatches],
      status: t.status,
    });
    setIsModalOpen(true);
  };

  const handleToggleLevel = (lvl: string) => {
    if (formData.assignedLevels.includes(lvl)) {
      setFormData({
        ...formData,
        assignedLevels: formData.assignedLevels.filter((l) => l !== lvl),
      });
    } else {
      setFormData({
        ...formData,
        assignedLevels: [...formData.assignedLevels, lvl],
      });
    }
  };

  const handleToggleBatch = (bat: string) => {
    if (formData.assignedBatches.includes(bat)) {
      setFormData({
        ...formData,
        assignedBatches: formData.assignedBatches.filter((b) => b !== bat),
      });
    } else {
      setFormData({
        ...formData,
        assignedBatches: [...formData.assignedBatches, bat],
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isAuditor) return;

    if (editingTeacher) {
      onUpdateTeacher({
        ...editingTeacher,
        ...formData,
      });
      addToast('success', 'Teacher Updated', `Updated workload and allocations for ${formData.name}`);
    } else {
      const newTeacher: AdminTeacher = {
        id: `TCH-${Math.floor(10 + Math.random() * 90)}`,
        ...formData,
        classesConducted: 0,
      };
      onAddTeacher(newTeacher);
      addToast('success', 'Teacher Created', `Enrolled ${newTeacher.name} into Pathshala faculty.`);
    }
    setIsModalOpen(false);
  };

  const handleDelete = (t: AdminTeacher) => {
    if (isAuditor) return;

    // Check if teacher is assigned to scheduled classes
    const activeAssignedClasses = classes.filter(
      (c) => c.teacherId === t.id && (c.status === 'Scheduled' || c.status === 'LIVE NOW')
    );

    if (activeAssignedClasses.length > 0) {
      addToast(
        'error',
        'Cannot Delete Faculty Member',
        `${t.name} is assigned to ${activeAssignedClasses.length} upcoming live class(es). Reassign classes first.`
      );
      return;
    }

    onDeleteTeacher(t.id);
    addToast('info', 'Teacher Removed', `Removed ${t.name} from faculty directory.`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="admin-card flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-[var(--gv-ink)]">Teachers & Gurujis Directory</h1>
        </div>

        {!isAuditor && (
          <Button leftIcon={UserPlus} onClick={openAddModal}>
            Add Faculty Member
          </Button>
        )}
      </div>

      {/* Teachers Cards Grid */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {teachers.map((t) => (
          <div
            key={t.id}
            className="admin-card flex flex-col justify-between space-y-4 p-5 transition-all hover:border-[var(--gv-brand)]/30"
          >
            <div className="space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <span className="font-mono text-[10px] font-bold text-[var(--gv-brand)]">{t.id}</span>
                  <h3 className="text-base font-bold text-[var(--gv-ink)]">{t.name}</h3>
                  <p className="text-xs font-medium text-[var(--gv-ink-muted)]">{t.title}</p>
                </div>
                <Badge variant={t.status === 'Active' ? 'success' : 'outline'}>
                  {t.status}
                </Badge>
              </div>

              {/* Workload Badges */}
              <div className="space-y-2 pt-1 text-xs">
                <div className="rounded-lg border border-[var(--gv-border)] bg-[var(--gv-surface)] p-2.5">
                  <span className="mb-1 block font-mono text-[10px] uppercase text-[var(--gv-ink-muted)]">Assigned Levels</span>
                  <div className="flex flex-wrap gap-1">
                    {t.assignedLevels.map((lvl, i) => (
                      <Badge key={i} variant="warning" className="normal-case">
                        {lvl}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg border border-[var(--gv-border)] bg-[var(--gv-surface)] p-2.5">
                  <span className="mb-1 block font-mono text-[10px] uppercase text-[var(--gv-ink-muted)]">Assigned Batches</span>
                  <div className="flex flex-wrap gap-1">
                    {t.assignedBatches.map((bat, i) => (
                      <Badge key={i} variant="outline" className="normal-case">
                        {bat}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-1 font-mono text-[11px] text-[var(--gv-ink-muted)]">
                  <span>Classes Conducted:</span>
                  <span className="font-bold text-[var(--gv-ink)]">{t.classesConducted} sessions</span>
                </div>
              </div>
            </div>

            {/* Direct Communication & Actions Bar */}
            <div className="flex items-center justify-between border-t border-[var(--gv-border)] pt-3">
              <div className="flex items-center gap-1.5">
                <Tooltip content="Call Instructor">
                  <a
                    href={`tel:${t.mobile}`}
                    className="inline-flex cursor-pointer rounded-md border border-[var(--gv-border)] bg-[var(--gv-surface)] p-1.5 text-[var(--gv-ink-muted)] transition-colors hover:bg-stone-100"
                  >
                    <Phone className="h-3.5 w-3.5 text-emerald-600" />
                  </a>
                </Tooltip>
                <Tooltip content="WhatsApp Message">
                  <a
                    href={`https://wa.me/${t.mobile.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex cursor-pointer rounded-md border border-[var(--gv-border)] bg-[var(--gv-surface)] p-1.5 text-[var(--gv-ink-muted)] transition-colors hover:bg-stone-100"
                  >
                    <MessageSquare className="h-3.5 w-3.5 text-emerald-600" />
                  </a>
                </Tooltip>
                <Tooltip content="Email">
                  <a
                    href={`mailto:${t.email}`}
                    className="inline-flex cursor-pointer rounded-md border border-[var(--gv-border)] bg-[var(--gv-surface)] p-1.5 text-[var(--gv-ink-muted)] transition-colors hover:bg-stone-100"
                  >
                    <Mail className="h-3.5 w-3.5 text-[var(--gv-brand)]" />
                  </a>
                </Tooltip>
              </div>

              {!isAuditor && (
                <div className="flex items-center gap-1">
                  <Tooltip content="Edit Faculty">
                    <Button
                      variant="ghost"
                      size="icon"
                      leftIcon={Edit2}
                      aria-label={`Edit ${t.name}`}
                      onClick={() => openEditModal(t)}
                    />
                  </Tooltip>
                  <Tooltip content="Delete Faculty">
                    <Button
                      variant="ghost"
                      size="icon"
                      leftIcon={Trash2}
                      aria-label={`Delete ${t.name}`}
                      className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                      onClick={() => handleDelete(t)}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
          <div className="admin-card w-full max-w-lg space-y-4 p-6 text-[var(--gv-ink)] shadow-xl">
            <div className="flex items-center justify-between border-b border-[var(--gv-border)] pb-3">
              <h3 className="text-base font-bold text-[var(--gv-ink)]">
                {editingTeacher ? `Edit Teacher: ${editingTeacher.name}` : 'Add New Faculty Member'}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => setIsModalOpen(false)}
              />
            </div>

            <form onSubmit={handleSubmit} className="space-y-3 text-xs">
              <Input
                label="Full Name & Title"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Rajvi Ben Shah"
              />

              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Role / Title"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Senior Guruji"
                />
                <Input
                  label="Mobile Number"
                  required
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  placeholder="+91 98200 00000"
                />
              </div>

              <Input
                label="Email Address"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="teacher@jainpathshala.org"
              />

              <div>
                <label className="mb-1.5 block text-xs font-bold text-[var(--gv-ink)]">
                  Assigned Pathshala Levels
                </label>
                <div className="space-y-1.5 rounded-lg border border-[var(--gv-border)] bg-[var(--gv-surface)] p-3">
                  {availableLevels.map((lvl) => (
                    <label key={lvl} className="flex cursor-pointer items-center gap-2 font-medium text-[var(--gv-ink-muted)]">
                      <input
                        type="checkbox"
                        checked={formData.assignedLevels.includes(lvl)}
                        onChange={() => handleToggleLevel(lvl)}
                        className="rounded border-[var(--gv-border)] text-[var(--gv-brand)] focus:ring-[var(--gv-brand)]"
                      />
                      <span>{lvl}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-bold text-[var(--gv-ink)]">
                  Assigned Batch Slots
                </label>
                <div className="space-y-1.5 rounded-lg border border-[var(--gv-border)] bg-[var(--gv-surface)] p-3">
                  {availableBatches.map((bat) => (
                    <label key={bat} className="flex cursor-pointer items-center gap-2 font-medium text-[var(--gv-ink-muted)]">
                      <input
                        type="checkbox"
                        checked={formData.assignedBatches.includes(bat)}
                        onChange={() => handleToggleBatch(bat)}
                        className="rounded border-[var(--gv-border)] text-[var(--gv-brand)] focus:ring-[var(--gv-brand)]"
                      />
                      <span>{bat}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save Faculty Profile</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
